import type { DomainConfigInputSource } from './domain-config-input.util';
import { DomainConfigInputSchema } from './domain-config.schema';
import {
  buildApplicationDomainsFromScope,
  getHostnameFromApplicationUrl,
  getHostnameFromDomainValue,
} from './build-application-domains.util';
import type { z } from 'zod';

type DomainValidationContext = Pick<z.RefinementCtx, 'addIssue'>;

const parseBuiltDomainEntry = (entry: DomainConfigInputSource) =>
  DomainConfigInputSchema.safeParse({
    ...entry,
    pathRules: entry.pathRules ?? [],
  });

const resolveDomainIssuePath = ({
  applicationUrl,
  domains,
  entryDomain,
}: {
  applicationUrl: string;
  domains: DomainConfigInputSource[];
  entryDomain: string;
}): (string | number)[] => {
  const urlHostname =
    getHostnameFromApplicationUrl(applicationUrl)?.toLowerCase();
  const entryHostname = getHostnameFromDomainValue(entryDomain)?.toLowerCase();

  if (urlHostname && entryHostname === urlHostname) {
    return ['url'];
  }

  const domainIndex = domains.findIndex((domainEntry) => {
    const host = getHostnameFromDomainValue(domainEntry.domain)?.toLowerCase();
    return host === entryHostname;
  });

  if (domainIndex >= 0) {
    return ['domains', domainIndex, 'domain'];
  }

  return ['domains'];
};

export const refineBuiltApplicationDomains = ({
  applicationUrl,
  domains = [],
  ctx,
  pathPrefix = [],
}: {
  applicationUrl: string;
  domains?: DomainConfigInputSource[];
  ctx: DomainValidationContext;
  pathPrefix?: (string | number)[];
}): void => {
  const builtDomains = buildApplicationDomainsFromScope({
    applicationUrl,
    domains,
  });

  for (const entry of builtDomains) {
    const parsed = parseBuiltDomainEntry(entry);
    if (parsed.success) {
      continue;
    }

    const relativePath = resolveDomainIssuePath({
      applicationUrl,
      domains,
      entryDomain: entry.domain,
    });

    for (const issue of parsed.error.issues) {
      ctx.addIssue({
        code: 'custom',
        message: issue.message,
        path: [...pathPrefix, ...relativePath],
      });
    }
  }
};
